/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { listAccounts, onboardComplete } from "../api/ctraderApi";

const SelectCTraderAccount = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [debug, setDebug] = useState(null);
  const [internalChoice, setInternalChoice] = useState("");

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tempId = queryParams.get("ctrader_temp_id") || queryParams.get("temp_id") || queryParams.get("state");
    if (!tempId) return;
    (async () => {
      try {
        const data = await listAccounts(tempId);
        setDebug({ raw: data });

        // Try to recover internal account id if missing
        const qInternal = queryParams.get("internal_account_id") || queryParams.get("account_id") || queryParams.get("internal_id");
        const fromResponse = data?.internal_account_id || data?.account_id || data?.internal_id || data?.accountId;
        let internalAccountId = qInternal || fromResponse || localStorage.getItem("ctrader_pending_internal_account_id");
        if (internalAccountId) {
          localStorage.setItem("ctrader_pending_internal_account_id", String(internalAccountId));
          setInternalChoice(String(internalAccountId));
        } else {
          // Try to retrieve from user accounts (fallback)
          try { /* intentionally no fallback UI */ } catch { /* noop */ }
        }

        const rawList = data?.accounts ?? data?.data ?? data ?? [];
        const normalized = (Array.isArray(rawList) ? rawList : []).map((a) => {
          const id = a?.accountId ?? a?.ctidTraderAccountId ?? a?.traderAccountId ?? a?.ctrader_account_id ?? a?.id ?? a?.account_id;
          const number = a?.accountNumber ?? a?.login ?? a?.account ?? a?.account_number;
          const broker = a?.broker ?? a?.brokerName ?? a?.broker_name;
          const currency = a?.currency ?? a?.balanceCurrency ?? a?.ccy;
          const balance = a?.balance ?? a?.cashBalance ?? a?.equity ?? a?.available_balance;
          const env = a?.environment ?? a?.env ?? a?.type;
          return { id, number, broker, currency, balance, env, raw: a };
        });
        setAccounts(normalized);
  } catch (e) {
        console.error("Failed to fetch cTrader accounts after callback", e);
        setAccounts([]);
      }
    })();
  }, [location]);

  const handleAccountSelect = async (ctraderAccountId) => {
    try {
      // Try multiple sources for the internal account id
      const internalAccountId =
        localStorage.getItem("ctrader_pending_internal_account_id") ||
        sessionStorage.getItem("ctrader_pending_internal_account_id") ||
        internalChoice ||
        new URLSearchParams(location.search).get("internal_account_id") ||
        new URLSearchParams(location.search).get("account_id");
      const queryParams = new URLSearchParams(location.search);
      const tempId = queryParams.get("ctrader_temp_id") || queryParams.get("temp_id") || queryParams.get("state");
      if (!internalAccountId || !tempId) {
        alert("Missing onboarding context. Please restart cTrader connect.");
        return;
      }
      await onboardComplete(internalAccountId, { temp_id: tempId, ctrader_account_id: ctraderAccountId });
      // Cleanup and navigate
  localStorage.removeItem("ctrader_pending_internal_account_id");
      navigate("/accounts");
    } catch (error) {
      console.error("❌ Failed to complete cTrader onboarding:", error);
      alert(error?.message || "Failed to complete onboarding");
    }
  };
  

  return (
    <div>
      <h2>Select a cTrader Account</h2>
  {/* internal account id is resolved from saved state; no dropdown UI */}

      {accounts.length > 0 ? (
        <ul>
          {accounts.map((a) => {
            const id = a.id;
            const label = a.number || [a.broker, a.env && `(${a.env})`, id != null && `id:${id}`].filter(Boolean).join(' ') || `Account ${id ?? ''}`;
            return (
              <li key={id ?? Math.random()}>
                <button onClick={() => handleAccountSelect(id)}>
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p>No accounts found.</p>
      )}
      {/* Optional debug for unexpected payloads */}
      {debug && (
        <pre style={{ marginTop: 16, opacity: 0.6 }}>
          {(() => {
            try { return JSON.stringify(debug, null, 2); } catch { return null; }
          })()}
        </pre>
      )}
    </div>
  );
};



export default SelectCTraderAccount;
